/**
 * Turn a PDF's pages into photos in the browser, so a large menu PDF can be
 * uploaded one page at a time (the server rejects bodies over ~1MB).
 * pdf.js is loaded from the CDN only when a PDF is actually chosen.
 */
const PDFJS_BASE = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174";
const TARGET_BYTES = 900 * 1024;
const MAX_DIMENSION = 2200; // keeps small menu text readable
export const MAX_PDF_PAGES = 10;

let pdfjsPromise: Promise<any> | null = null;
const loadPdfJs = () => {
  if ((window as any).pdfjsLib) return Promise.resolve((window as any).pdfjsLib);
  if (!pdfjsPromise) {
    pdfjsPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `${PDFJS_BASE}/pdf.min.js`;
      script.onload = () => {
        const lib = (window as any).pdfjsLib;
        lib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
        resolve(lib);
      };
      script.onerror = () => {
        pdfjsPromise = null;
        reject(new Error("Couldn't load the PDF reader — check your connection"));
      };
      document.head.appendChild(script);
    });
  }
  return pdfjsPromise;
};

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

/** Returns one image File per page (up to MAX_PDF_PAGES). */
export const pdfToImages = async (file: File): Promise<File[]> => {
  const pdfjs = await loadPdfJs();
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const pageCount = Math.min(pdf.numPages, MAX_PDF_PAGES);
  const baseName = file.name.replace(/\.pdf$/i, "");
  const images: File[] = [];

  for (let n = 1; n <= pageCount; n += 1) {
    const page = await pdf.getPage(n);
    const unscaled = page.getViewport({ scale: 1 });
    let scale = MAX_DIMENSION / Math.max(unscaled.width, unscaled.height);

    // Shrink until the page fits under the upload limit.
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;

      const blob = await toBlob(canvas, "image/jpeg", Math.max(0.6, 0.88 - attempt * 0.06));
      if (blob && (blob.size <= TARGET_BYTES || attempt === 5)) {
        images.push(new File([blob], `${baseName}-page-${n}.jpg`, { type: "image/jpeg" }));
        break;
      }
      scale *= 0.85;
    }
  }
  return images;
};
